import React, { useMemo } from 'react';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { Switch } from 'antd';
import {
  ActionItem,
  ActionLabel,
  Container,
  Dropdown,
  Tooltip
} from './styled';
import RadioGroup from '../../../components/RadioGroup';
import RangeDatePicker from '../../../components/RangeDatePicker';

const DURATION_ITEMS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
  { label: '180 Days', value: 180 },
  { label: '360 Days', value: 360 }
];

const TransactionDetailsFilters = ({
  hasTriggeredOnlySwitch,
  hasTypologiesFilter,
  typologies = [],
  showLabels,
  filters,
  onFilterChange,
  alertDateTO
}) => {
  const typologiesOptions = useMemo(
    () => [
      {
        id: 'all',
        name: filters?.isTriggeredOnly ? 'All' : ''
      },
      ...typologies?.map(({ id, name }) => ({
        id,
        name
      }))
    ],
    [typologies, filters.isTriggeredOnly]
  );

  const onChangeTriggeredOnly = (value) => {
    onFilterChange({
      ...filters,
      isTriggeredOnly: value,
      typologyId: value ? filters.typologyId : 'all'
    });
  };

  const onRadioGroupChange = (days) => {
    const from = cloneDeep(alertDateTO).subtract(days - 1, 'days');

    onFilterChange({
      ...filters,
      duration: days,
      from,
      to: alertDateTO
    });
  };

  const onDatePickerChange = (range) => {
    onFilterChange({
      ...filters,
      duration: null,
      from: range[0],
      to: range[1]
    });
  };

  const disableDate = (current) => {
    return current > alertDateTO.endOf('day');
  };

  const onTypologyChange = (typology) => {
    onFilterChange({
      ...filters,
      typologyId: typology.id
    });
  };

  return (
    <Container>
      {hasTriggeredOnlySwitch && (
        <ActionItem>
          {showLabels && <ActionLabel>Triggered Only</ActionLabel>}
          <Switch
            checked={filters?.isTriggeredOnly}
            onChange={onChangeTriggeredOnly}
          />
        </ActionItem>
      )}
      <ActionItem>
        {showLabels && <ActionLabel>Duration</ActionLabel>}
        <RadioGroup
          items={DURATION_ITEMS}
          defaultValue={filters?.duration}
          onChange={onRadioGroupChange}
          size="small"
        />
      </ActionItem>
      <ActionItem>
        {showLabels && <ActionLabel>Date Range</ActionLabel>}
        <RangeDatePicker
          defaultDates={[filters?.from, filters?.to]}
          onChange={onDatePickerChange}
          size="small"
          disabledDate={disableDate}
        />
      </ActionItem>
      {hasTypologiesFilter && (
        <ActionItem>
          {showLabels && <ActionLabel>Typologies</ActionLabel>}
          <Dropdown
            disabled={!filters?.isTriggeredOnly}
            dropdownValue={typologiesOptions.find(
              ({ id }) => id === filters?.typologyId
            )}
            defaultValue={`all`}
            dropdownOptions={typologiesOptions?.map((option) => ({
              ...option,
              name: <Tooltip content={option.name}>{option.name}</Tooltip>
            }))}
            handleValueChange={onTypologyChange}
          />
        </ActionItem>
      )}
    </Container>
  );
};

TransactionDetailsFilters.defaultProps = {
  hasTriggeredOnlySwitch: true,
  hasTypologiesFilter: true,
  showLabels: true,
  filters: []
};

TransactionDetailsFilters.propTypes = {
  hasTriggeredOnlySwitch: PropTypes.bool,
  hasTypologiesFilter: PropTypes.bool,
  typologies: PropTypes.array,
  showLabels: PropTypes.bool,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  alertDateTO: PropTypes.object
};

export default TransactionDetailsFilters;
